import { invalidateRevitParametersStore } from '../../../stores/useRevitParametersStore';
import {
  type AvailableProjectParameterDescriptor,
  normalizeProjectParameterCategories,
  type ParameterBindingKind,
  type ParameterTypeHint,
  toProjectParameterType,
} from '../../revit/projectParameterMutationPlan';
import { registerRevitModelCacheInvalidator } from '../../revit/modelCacheInvalidation';
import { getEquivalentRevitParameterNames, sanitizeRevitParameterNames } from '../../revitParameterNames';
import { type AvailableProjectParameterFetchOptions, type ParameterAvailabilityCheckOptions } from './models';

export const DEFAULT_PARAMETER_CACHE_SCOPE = '__active_revit_document__';

export const availableProjectParameterCache = new Map<
  string,
  AvailableProjectParameterDescriptor[]
>();

export function getAvailableParameterCacheKey(
  cacheScopeKey?: string | null,
): string {
  return cacheScopeKey?.trim() || DEFAULT_PARAMETER_CACHE_SCOPE;
}

export function cloneAvailableProjectParameters(
  parameters: AvailableProjectParameterDescriptor[],
): AvailableProjectParameterDescriptor[] {
  return parameters.map((parameter) => ({
    ...parameter,
    ...(parameter.categories ? { categories: [...parameter.categories] } : {}),
  }));
}

export function normalizeParameterAvailabilityOptions(
  options?: ParameterAvailabilityCheckOptions,
): AvailableProjectParameterFetchOptions {
  if (typeof options === 'string' || options === null) {
    return {
      cacheScopeKey: options ?? null,
    };
  }

  return options ?? {};
}

export function getParameterDescriptorKey(
  parameter: Pick<
    AvailableProjectParameterDescriptor,
    'name' | 'parameterType'
  >,
): string {
  return `${parameter.name.trim().toLowerCase()}|${parameter.parameterType}`;
}

export function getEquivalentParameterLookupNames(
  parameterName: string,
): string[] {
  return getEquivalentRevitParameterNames(parameterName)
    .map((name) => name.trim().toLowerCase())
    .filter(Boolean);
}

export function projectParameterCategoriesOverlap(
  leftCategories: string[],
  rightCategories: unknown,
): boolean {
  const normalizedRightCategories = normalizeProjectParameterCategories(
    rightCategories,
  );
  if (leftCategories.length === 0 || normalizedRightCategories.length === 0) {
    return true;
  }

  const rightCategorySet = new Set(
    normalizedRightCategories.map((category) => category.toLowerCase()),
  );
  return leftCategories.some((category) => rightCategorySet.has(category.toLowerCase()));
}

export function clearAvailableProjectParameterCache(
  cacheScopeKey?: string | null,
): void {
  if (typeof cacheScopeKey === 'undefined') {
    availableProjectParameterCache.clear();
    return;
  }

  availableProjectParameterCache.delete(
    getAvailableParameterCacheKey(cacheScopeKey),
  );
}

export function readAvailableProjectParameterCache(
  cacheScopeKey?: string | null,
): AvailableProjectParameterDescriptor[] | null {
  const cached = availableProjectParameterCache.get(
    getAvailableParameterCacheKey(cacheScopeKey),
  );
  return cached ? cloneAvailableProjectParameters(cached) : null;
}

export function rememberAvailableProjectParameters(
  parameterNames: string[],
  bindingKind: ParameterBindingKind,
  cacheScopeKey?: string | null,
  categories?: string[],
  parameterTypeHints: ParameterTypeHint[] = [],
): void {
  const cacheKey = getAvailableParameterCacheKey(cacheScopeKey);
  const existing = availableProjectParameterCache.get(cacheKey) ?? [];
  const descriptorsByKey = new Map(
    existing.map((
      parameter,
    ) => [getParameterDescriptorKey(parameter), { ...parameter }]),
  );
  const parameterType = toProjectParameterType(bindingKind);
  const normalizedCategories = normalizeProjectParameterCategories(categories);

  sanitizeRevitParameterNames(parameterNames).forEach((name) => {
    const equivalentNames = new Set(getEquivalentParameterLookupNames(name));
    const hintedDataType = parameterTypeHints.find((hint) =>
      equivalentNames.has(hint.name.trim().toLowerCase())
    )?.dataType.trim();
    Array.from(descriptorsByKey.entries()).forEach(([key, descriptor]) => {
      if (
        equivalentNames.has(descriptor.name.trim().toLowerCase()) &&
        descriptor.parameterType !== parameterType &&
        projectParameterCategoriesOverlap(
          normalizedCategories,
          descriptor.categories,
        )
      ) {
        descriptorsByKey.delete(key);
      }
    });

    const descriptorKey = getParameterDescriptorKey({ name, parameterType });
    const existingDataType = descriptorsByKey.get(descriptorKey)?.dataType?.trim();
    const dataType = hintedDataType || existingDataType;
    descriptorsByKey.set(descriptorKey, {
      name,
      label: name,
      parameterType,
      group: 'Cached',
      isProjectBinding: true,
      ...(normalizedCategories.length > 0 ? { categories: normalizedCategories } : {}),
      ...(dataType ? { dataType } : {}),
    });
  });

  availableProjectParameterCache.set(
    cacheKey,
    Array.from(descriptorsByKey.values()),
  );
  invalidateRevitParametersStore();
}

export function forgetAvailableProjectParameters(
  parameterNames: string[],
  cacheScopeKey?: string | null,
): void {
  const cacheKey = getAvailableParameterCacheKey(cacheScopeKey);
  const cached = availableProjectParameterCache.get(cacheKey);
  if (!cached) {
    return;
  }

  const namesToForget = new Set(
    sanitizeRevitParameterNames(parameterNames).flatMap(
      getEquivalentParameterLookupNames,
    ),
  );

  availableProjectParameterCache.set(
    cacheKey,
    cached.filter((parameter) =>
      getEquivalentParameterLookupNames(parameter.name).every((name) => !namesToForget.has(name))
    ),
  );
}

registerRevitModelCacheInvalidator((event) => {
  if (event.eventType !== 'ModelChanged') {
    clearAvailableProjectParameterCache();
  }
});
